import React, { useMemo } from 'react';
import { Grid, Paper, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { useIntl } from 'react-intl';
import { useMessages } from 'modules/intl';
import { TimeSignalAcceptedBy, TimeSignalDimensionType } from 'graphql/types';
import { useGetTimeSignalAnalyticsData } from 'modules/Insights/Notes/hooks/useGetTimeSignalAnalyticsData';
import { useGetPreviousAnalyticsData } from 'modules/Insights/Notes/hooks/useGetPreviousAnalyticsData';
import { hasValidStartDate } from 'modules/Insights/utils';
import { InsightsTrends } from '../InsightsTrends';
import { AutoAcceptComponent } from './AutoAcceptComponent';
import { transformAnalyticsData } from './utils';
import { AutoAcceptSkeleton } from './AutoAcceptSkeleton';
import { AutoAcceptNoData } from './AutoAcceptNoData';

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(0, 2),
    marginRight: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    minWidth: '45%',
    justifyContent: 'center'
  },
  acceptContainer: {
    display: 'flex',
    flexDirection: 'column',
    margin: theme.spacing(1)
  },
  percentage: {
    color: theme.palette.text.primary,
    ...theme.typography.h6
  },
  grid: {
    display: 'flex',
    flexDirection: 'row'
  },
  span: {
    alignSelf: 'center',
    margin: theme.spacing(0, 3),
    color: theme.palette.text.secondary
  },
  icon: {
    color: theme.palette.success.main,
    marginLeft: theme.spacing(1)
  }
}));

const labels = ['insights.autoAccept.SYSTEM', 'insights.autoAccept.USER'];
export const AutoAccept: React.FC = () => {
  const { data, loading, isEmpty } = useGetTimeSignalAnalyticsData(
    TimeSignalDimensionType.MetricsByAutoaccept
  );

  const {
    data: previousData,
    loading: previousLoading,
    prevStartDate
  } = useGetPreviousAnalyticsData(TimeSignalDimensionType.MetricsByAutoaccept);

  const tranformData = transformAnalyticsData(data?.timeSignalAnalytics?.items);
  const prevTransformData = transformAnalyticsData(
    previousData?.timeSignalAnalytics?.items
  );
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const messages = useMessages(labels);

  const isStartDateValid = useMemo(() => {
    const validStartDate = hasValidStartDate(prevStartDate || '');
    return validStartDate;
  }, [prevStartDate]);

  return (
    <>
      {loading || previousLoading ? (
        <AutoAcceptSkeleton />
      ) : isEmpty ? (
        <AutoAcceptNoData />
      ) : (
        <Paper
          className={classes.container}
          elevation={3}
          data-testid={'auto-accept-container'}
        >
          <Grid flexWrap={'wrap'} className={classes.grid}>
            <AutoAcceptComponent
              label={messages['insights.autoAccept.SYSTEM']}
              element={
                <Typography
                  variant="h4"
                  className={classes.percentage}
                  data-testid="auto-accept-percentage-system"
                >
                  {`${tranformData[TimeSignalAcceptedBy.System]}%`}
                </Typography>
              }
              trendsElement={
                isStartDateValid ? (
                  <InsightsTrends
                    prevData={
                      prevTransformData[TimeSignalAcceptedBy.System] as number
                    }
                    currentData={
                      tranformData[TimeSignalAcceptedBy.System] as number
                    }
                    label={TimeSignalAcceptedBy.System}
                  />
                ) : (
                  <></>
                )
              }
            />
            <Typography
              variant="caption"
              whiteSpace="nowrap"
              className={classes.span}
              data-testid="auto-accept-percentage-system-message"
            >
              {formatMessage({ id: 'insights.autoAccept.vs' })}
            </Typography>
            <AutoAcceptComponent
              label={messages['insights.autoAccept.USER']}
              element={
                <Typography
                  variant="h4"
                  className={classes.percentage}
                  data-testid="auto-accept-percentage-user"
                >
                  {`${tranformData[TimeSignalAcceptedBy.User]}%`}
                </Typography>
              }
              trendsElement={
                isStartDateValid ? (
                  <InsightsTrends
                    prevData={
                      prevTransformData[TimeSignalAcceptedBy.User] as number
                    }
                    currentData={
                      tranformData[TimeSignalAcceptedBy.User] as number
                    }
                    label={TimeSignalAcceptedBy.User}
                  />
                ) : (
                  <></>
                )
              }
            />
          </Grid>
        </Paper>
      )}
    </>
  );
};
